// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { create } from 'zustand';
import type {
  SpectatorSettings,
  SpectatorGameState,
  JoinAsSpectatorResponse,
} from '../types/spectator';

interface SpectatorState {
  // Connection
  isSpectating: boolean;
  spectatorId: string | null;
  sessionId: string | null;

  // Settings
  settings: SpectatorSettings;

  // Live data
  spectatorCount: number;
  gameState: SpectatorGameState | null;

  // Actions
  joinAsSpectator: (sessionId: string, displayName: string) => Promise<void>;
  leaveSpectating: () => void;
  updateGameState: (state: SpectatorGameState) => void;
  setSpectatorCount: (count: number) => void;
  reset: () => void;
}

const defaultSettings: SpectatorSettings = {
  enabled: true,
  max_spectators: 100,
  delay_seconds: 0,
  allow_chat: false,
  show_player_names: true,
  show_hands_after_submit: false,
};

const _useSpectatorStore = create<SpectatorState>(set => ({
  isSpectating: false,
  spectatorId: null,
  sessionId: null,
  settings: defaultSettings,
  spectatorCount: 0,
  gameState: null,

  joinAsSpectator: async (sessionId: string, displayName: string) => {
    const response = await fetch(`/api/spectate/${sessionId}/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ display_name: displayName }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to join as spectator');
    }

    const data: JoinAsSpectatorResponse = await response.json();

    set({
      isSpectating: true,
      spectatorId: data.spectator_id,
      sessionId: sessionId,
      settings: data.settings,
      spectatorCount: data.spectator_count,
    });
  },

  leaveSpectating: () => {
    set({
      isSpectating: false,
      spectatorId: null,
      sessionId: null,
      gameState: null,
    });
  },

  updateGameState: (state: SpectatorGameState) => set({ gameState: state }),

  setSpectatorCount: (count: number) => set({ spectatorCount: count }),

  reset: () =>
    set({
      isSpectating: false,
      spectatorId: null,
      sessionId: null,
      settings: defaultSettings,
      spectatorCount: 0,
      gameState: null,
    }),
}));

// Expose for E2E testing
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__ZUSTAND_SPECTATOR_STORE__ = _useSpectatorStore;
}

export const useSpectatorStore = _useSpectatorStore;
